import React, { useMemo } from 'react';

import { getHrefLangData } from '../utils/hrefLangData';
import { normalizeURLSlashes } from '../utils/normalizeURLSlashes';

/** For use in Gatsby Head API only */
export function Meta({
  pageTitle,
  pageContext,
  title,
  description,
  url,
  canonicalUrl,
  noIndex,
  manualHrefLangs,
}) {
  const favicon = pageContext?.global?.favicon?.url;
  const social_share_image = pageContext?.global?.social_share_image?.url;
  const passedTitle = pageTitle || title;
  const { siteTag, siteName, siteLang: lang, siteURL } = pageContext;

  const getPatchedTitle = (passedTitle, siteName, siteTag) => {
    if (!passedTitle) {
      return siteName;
    }
    if (
      siteTag === 'site:kitchen-central' ||
      siteTag === 'site:cloudkitchens' ||
      passedTitle.includes(' | ') ||
      passedTitle.includes(' • ') ||
      passedTitle?.includes(' - ')
    ) {
      return passedTitle;
    }
    return `${passedTitle} | ${siteName}`;
  };

  const removeLangPrefix = (path) => {
    // Matches and removes the language code prefix (e.g., /ar-sa/, /en/, etc.)
    return path.replace(/^\/[a-z]{2}(-[a-z]{2})?\//, '/');
  };

  const removeDuplicateLocations = (url) => {
    const urlObj = new URL(url);
    let path = urlObj.pathname;

    path = path.replace(/\/locations\/locations/g, '/locations');

    urlObj.pathname = path;

    return urlObj.toString();
  };

  const patchedTitle = useMemo(
    () => getPatchedTitle(passedTitle, pageContext.siteName, siteTag),
    [passedTitle, pageContext.siteName, siteTag]
  );

  const normalizedUrl =
    typeof url === 'string' ? normalizeURLSlashes(url) || '' : '';
  const urlWithTrailingSlash = normalizedUrl.endsWith('/')
    ? normalizedUrl
    : `${normalizedUrl}/`;

  const patchedURL = useMemo(() => {
    return removeDuplicateLocations(urlWithTrailingSlash);
  }, [urlWithTrailingSlash]);
  const patchedCanonicalURL = normalizeURLSlashes(canonicalUrl);

  const pagePath = patchedURL
    ? removeLangPrefix(new URL(patchedURL).pathname)
    : '/';

  const patchedLang =
    lang.split('-')[0] === 'en'
      ? ['en-us', 'en-gb'].includes(lang.toLowerCase())
        ? lang
        : 'en-us'
      : lang;

  let ogLocale = patchedLang
    .split('-')
    .map((l, i) => (i === 0 ? l.toLowerCase() : l.toUpperCase()))
    .join('_');

  // Patch for language codes with only 2 characters, e.g. "id"
  if (ogLocale.length === 2) ogLocale = `${ogLocale}_${ogLocale.toUpperCase()}`;

  const htmlDir = ['ar', 'he'].includes(patchedLang.split('-')[0])
    ? 'rtl'
    : 'auto';

  const hrefLangData = getHrefLangData(
    siteURL,
    siteTag,
    pagePath,
    manualHrefLangs
  );

  return (
    <>
      <html dir={htmlDir} lang={patchedLang} />
      <title>{patchedTitle}</title>
      <meta name="description" content={description} />
      <link rel="icon" href={favicon} />
      <link rel="canonical" href={patchedCanonicalURL || patchedURL} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={social_share_image} />
      <meta property="og:image:secure_url" content={social_share_image} />
      <meta property="og:image:width" content="1204" />
      <meta property="og:image:height" content="800" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image" content={social_share_image} />
      {hrefLangData?.map((item, index) => {
        return (
          <link
            rel="alternate"
            hrefLang={item.hrefLang}
            href={item.href}
            key={index}
          />
        );
      })}
      {!noIndex ? (
        <meta
          name="robots"
          content="follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large"
        />
      ) : (
        <meta name="robots" content="noindex, nofollow" />
      )}
    </>
  );
}
